import type { ModalitaSistema, ModalitaVmc, Zona } from './impianto';
import type { MotivoFallimento } from './comandi';

/**
 * Qualità di una lettura (contratto API §10.2). È ciò che rende implementabile
 * RF-05: un valore mancante o sospetto non viene mai mostrato come attuale.
 */
export type QualitaLettura = 'ok' | 'sonda_scollegata' | 'sospetto' | 'mancante';

export interface Lettura {
  canale: string;
  ts: number;
  v: number | null;
  u: string;
  q: QualitaLettura;
}

export interface StatoAttuatore {
  canale: string;
  ts: number;
  stato: 'attivo' | 'spento';
  /** 0–100, solo per uscite modulanti. */
  modulazione?: number;
  /** Forzatura manuale in corso: istante di rientro automatico (RF-35). */
  forzatoFinoA?: number;
}

export type SeveritaAllarme = 'bassa' | 'media' | 'alta' | 'critica';

export type CodiceAllarme =
  | 'SONDA_GUASTA'
  | 'SONDA_SCOLLEGATA'
  | 'DELTA_T_ANOMALO'
  | 'SOVRATEMPERATURA'
  | 'GENERATORE_NON_PARTE'
  | 'CENTRALINA_IRRAGGIUNGIBILE'
  | 'SORGENTE_IRRAGGIUNGIBILE'
  | 'PUNTO_DI_RUGIADA';

export interface Allarme {
  id: string;
  ts: number;
  codice: CodiceAllarme;
  severita: SeveritaAllarme;
  stato: 'aperto' | 'riconosciuto' | 'chiuso';
  /** Canale, zona o sorgente interessati. */
  oggetto?: string;
  riconosciutoDa?: string;
  riconosciutoIl?: number;
  chiusoIl?: number;
}

export interface EventoStato {
  ts: number;
  tipo: string;
  descrizione: string;
}

/** Istantanea energetica (connettore fotovoltaico — interfaccia unificata §05). */
export interface Energia {
  ts: number;
  produzioneW: number;
  consumoW: number;
  batteriaPct: number;
  /** Positivo = prelievo dalla rete, negativo = immissione. */
  reteW: number;
  /** Positivo = carica, negativo = scarica. */
  batteriaW: number;
  q: QualitaLettura;
}

export interface StatoSorgente {
  id: string;
  raggiungibile: boolean;
  ultimoAggiornamento: number | null;
}

export interface StatoSanitario {
  setpoint: number;
  boostFinoA: number | null;
  inCarica: boolean;
  /**
   * true quando la sorgente espone solo acceso/spento e nessuna temperatura
   * (es. termostati di marca via cloud del produttore — ADR-060). La UI mostra
   * uno stato in sola lettura, non un controllo di temperatura, e `setpoint`
   * va ignorato. Assente per una centralina propria, che regola a temperatura.
   */
  soloOnOff?: boolean;
}

export interface StatoVmc {
  modalita: ModalitaVmc;
  attiva: boolean;
  modulazione?: number;
}

/** Stato di sintesi pubblicato dalla centralina a ogni cambiamento. */
export interface StatoImpianto {
  ts: number;
  modalita: ModalitaSistema;
  zone: Zona[];
  sanitario: StatoSanitario;
  vmc: StatoVmc;
  /** Zone attualmente in domanda (richiedono calore o freddo). */
  zoneInDomanda: string[];
}

export type EventoDispositivo =
  | { tipo: 'letture'; ts: number; valori: Record<string, Lettura> }
  | { tipo: 'attuatore'; ts: number; stato: StatoAttuatore }
  | { tipo: 'esito_comando'; ts: number; idComando: string; esito: 'accettato' | 'applicato' | 'fallito'; motivo?: MotivoFallimento }
  | { tipo: 'allarme'; ts: number; allarme: Allarme }
  | { tipo: 'stato'; ts: number; stato: StatoImpianto }
  | { tipo: 'energia'; ts: number; sorgenteId: string; energia: Energia }
  | { tipo: 'sorgente'; ts: number; sorgente: StatoSorgente }
  | { tipo: 'evento'; ts: number; evento: EventoStato };

/** Oltre questo silenzio il dato è obsoleto (monitoraggio.md, scenario "centralina irraggiungibile"). */
export const SOGLIA_OBSOLESCENZA_MS = 60_000;

/**
 * La stessa domanda, per una sorgente di terze parti interrogata a intervalli.
 *
 * 🚨 Sessanta secondi è la soglia giusta per una centralina che PUBBLICA a ogni
 * variazione: se tace un minuto, qualcosa non va. Una sorgente cloud è la forma
 * opposta — la si raggiunge ogni pochi minuti, e deve essere così, o i limiti
 * di frequenza del produttore ci chiudono fuori (ADR-060 §3). Applicarle la
 * soglia della centralina dipinge come permanentemente obsoleto un impianto che
 * funziona: è esattamente ciò che è successo, con la casa a trattini e il
 * banner "La centralina non risponde" sopra due letture fresche.
 *
 * Il valore è il gemello di `SOGLIA_FRESCHEZZA_CLOUD_MS` del connettore lato
 * server, e le due soglie vivono a strati diversi apposta: quella sorveglia un
 * flusso, questa un sondaggio.
 */
export const SOGLIA_OBSOLESCENZA_CLOUD_MS = 15 * 60 * 1000;

export function isObsoleto(
  ultimaLettura: number | null,
  adesso: number,
  soglia: number = SOGLIA_OBSOLESCENZA_MS,
): boolean {
  return ultimaLettura === null || adesso - ultimaLettura > soglia;
}
